/**
 * Standard course-builder walkthrough.
 * Shape: one unit, one lesson in it, and one of each activity in that lesson.
 * Similar activities share an editor pass. Activity-settings steps are omitted
 * when this person cannot open that gear.
 */

import type { MascotPose } from "@/pages/student/Mascot";

const STORAGE_KEY = "acuity-course-builder-walkthrough";

export type WalkthroughRecord = "dismissed" | "done";

export type WalkEditorPanel = "continue" | "submission" | "settings";

export type WalkStep = {
  id: string;
  /** `[data-tour]` value to shine on. */
  target: string;
  /** Tried in order when the main target is not on screen yet. */
  fallback?: string[];
  pose: MascotPose;
  text: string;
  focus?: boolean;
  /** Open the first activity of one of these types. `"*"` is any activity. */
  editorTypes?: string[];
  editorPanel?: WalkEditorPanel;
  /** Open the add menu on the course or the first unit. */
  openAdd?: "course" | "unit";
  /** Open the item-type picker on the first lesson. */
  openPicker?: boolean;
  /** Expand the first unit, and the first lesson when "lesson". */
  expand?: "unit" | "lesson";
};

export type WalkChapter = {
  id: string;
  title: string;
  steps: WalkStep[];
};

const details = (step: Omit<WalkStep, "pose"> & { pose?: MascotPose }): WalkStep => ({
  pose: "pointing",
  ...step,
});

function editorFallbacks(types: string[]) {
  return types.filter((type) => type !== "*").flatMap((type) => [`cb-edit-${type}`, `cb-type-${type}`]).concat("cb-add-lesson");
}

export function buildWalkthrough(opts: { canOverrideDefaults: boolean }): WalkChapter[] {
  const settings: WalkChapter[] = opts.canOverrideDefaults
    ? [
        {
          id: "activity-settings",
          title: "Activity settings",
          steps: [
            details({
              id: "settings-gear",
              target: "cb-item-settings",
              fallback: editorFallbacks(["*"]),
              editorTypes: ["*"],
              text: "The gear is Activity settings. It is here because you can change defaults. Other people never see this button.",
            }),
            details({
              id: "settings-includes",
              target: "cb-settings-includes",
              fallback: ["cb-item-settings"],
              editorTypes: ["*"],
              editorPanel: "settings",
              text: "Due date, time limit, and max attempts start on for an assessment, homework, and exam. Time limit starts off. Reading, video, audio, slides, and key terms start with all three off. A project, lab, or discussion starts with a due date only. Turn one on and its field shows on the form. Empty max attempts means unlimited.",
            }),
            details({
              id: "settings-questions",
              target: "cb-settings-questions",
              fallback: ["cb-settings-includes"],
              editorTypes: ["*"],
              editorPanel: "settings",
              text: "Include questions starts on for assessment, homework, and exam, and off for the rest. Require Knowledge starts on: they must submit those questions before Complete.",
            }),
            details({
              id: "settings-points",
              target: "cb-settings-points",
              fallback: ["cb-settings-includes"],
              editorTypes: ["*"],
              editorPanel: "settings",
              text: "Include points grades an activity that has no questions. It starts off, except a discussion, which stays on. Question activities take points from the questions instead.",
            }),
            details({
              id: "settings-gates",
              target: "cb-settings-gates",
              fallback: ["cb-settings-includes"],
              editorTypes: ["*"],
              editorPanel: "settings",
              text: "Required starts on, so they must finish it. Require previous starts on for a reading and anything like it: the item before must be done first. Homework, quizzes, exams, and discussions do not lock what comes after them.",
            }),
            details({
              id: "settings-chat",
              target: "cb-settings-chat",
              fallback: ["cb-settings-includes"],
              editorTypes: ["*"],
              editorPanel: "settings",
              text: "Student + teacher chat starts on. They can write to you on this activity. They can always read the thread.",
            }),
            details({
              id: "settings-board",
              target: "cb-settings-board",
              fallback: ["cb-type-discussion", "cb-settings-includes"],
              editorTypes: ["discussion"],
              editorPanel: "settings",
              text: "On a discussion, three switches start on: they may answer the prompt, they may reply to each other, and they must answer before they can read other replies.",
            }),
          ],
        },
      ]
    : [];

  return [
    {
      id: "details",
      title: "Course details",
      steps: [
        details({
          id: "details",
          target: "cb-details",
          pose: "waving",
          text: "We'll start with Course details. This is the title, syllabus, objectives, grades, and rubric for the course.",
        }),
        details({
          id: "title",
          target: "cb-title",
          focus: true,
          text: "Give the course a title students will recognize.",
        }),
        details({
          id: "description",
          target: "cb-description",
          focus: true,
          text: "A short description of what this course covers. You can leave it blank.",
        }),
        details({
          id: "category",
          target: "cb-category",
          text: "Choose a primary category so the course sits with the right subject.",
        }),
        details({
          id: "syllabus",
          target: "cb-syllabus",
          text: "Upload a PDF syllabus, or pick one you already have. Students open it on the Syllabus tab.",
        }),
        details({
          id: "objectives",
          target: "cb-objectives",
          focus: true,
          text: "Write one objective per line. Students see these on their Path.",
        }),
        details({
          id: "grades",
          target: "cb-grades",
          text: "Choose the grades this course is for. All, none, or just the ones you need.",
        }),
        details({
          id: "rubric",
          target: "cb-rubric",
          text: "The grade rubric weights the course. None means unweighted points on marked work.",
        }),
      ],
    },
    {
      id: "outline",
      title: "Outline",
      steps: [
        details({
          id: "outline-shape",
          target: "cb-outline",
          pose: "waving",
          text: "A unit holds lessons. A lesson holds activities. The standard set is one unit, one lesson inside it, and one of each activity in that lesson.",
        }),
        details({
          id: "outline-add",
          target: "cb-add-course",
          text: "Add from here. A unit, a lesson, or an item can also sit on the course itself. Start with a unit.",
        }),
      ],
    },
    {
      id: "unit",
      title: "Unit",
      steps: [
        details({
          id: "unit-pick",
          target: "cb-pick-unit",
          fallback: ["cb-add-course"],
          openAdd: "course",
          text: "Choose Unit. A unit is the block students move through. Lessons go inside it, and activities go inside those lessons.",
        }),
        details({
          id: "unit-name",
          target: "cb-unit-title",
          fallback: ["cb-name-prompt", "cb-unit", "cb-add-course"],
          text: "This is the unit title. Double-click it to name or rename it. A single click opens the unit.",
        }),
        details({
          id: "unit-row",
          target: "cb-unit",
          fallback: ["cb-unit-title", "cb-add-course"],
          expand: "unit",
          text: "This is the unit. Single-click the row to collapse or expand the lessons inside it. The lesson sits inside the unit, not beside it.",
        }),
      ],
    },
    {
      id: "lesson",
      title: "Lesson",
      steps: [
        details({
          id: "lesson-pick",
          target: "cb-pick-lesson",
          fallback: ["cb-add-unit", "cb-unit"],
          openAdd: "unit",
          expand: "unit",
          text: "Choose Lesson. A lesson lives in the unit. It is where the activities go.",
        }),
        details({
          id: "lesson-name",
          target: "cb-lesson-title",
          fallback: ["cb-name-prompt", "cb-lesson", "cb-add-unit"],
          expand: "unit",
          text: "This is the lesson title. Double-click it to name or rename it. A single click opens the lesson.",
        }),
        details({
          id: "lesson-row",
          target: "cb-lesson",
          fallback: ["cb-lesson-title", "cb-add-unit", "cb-unit"],
          expand: "lesson",
          text: "This is the lesson. Single-click the row to collapse or expand the activities inside it. Add one of each type here.",
        }),
      ],
    },
    {
      id: "reading",
      title: "Reading",
      steps: [
        details({
          id: "add-reading",
          target: "cb-type-reading",
          fallback: ["cb-add-lesson", "cb-lesson"],
          openPicker: true,
          expand: "lesson",
          text: "Add a reading. The student reads what you write in the body. Video, audio, and slides start from this same body.",
        }),
        details({
          id: "reading-title",
          target: "cb-item-title",
          fallback: editorFallbacks(["reading"]),
          editorTypes: ["reading"],
          expand: "lesson",
          focus: true,
          text: "Every activity has a title. This one is the reading's name on the outline and on Path.",
        }),
        details({
          id: "reading-minutes",
          target: "cb-item-minutes",
          fallback: ["cb-item-title"],
          editorTypes: ["reading"],
          expand: "lesson",
          text: "Estimated minutes count toward the course hours. The categories under the field split those minutes. Every activity has this.",
        }),
        details({
          id: "reading-body",
          target: "cb-item-body",
          fallback: ["cb-item-title"],
          editorTypes: ["reading"],
          expand: "lesson",
          text: "The body is the reading. A reading is incomplete until there is something here.",
        }),
      ],
    },
    {
      id: "media",
      title: "Video & audio",
      steps: [
        details({
          id: "add-video",
          target: "cb-type-video",
          fallback: ["cb-add-lesson", "cb-lesson"],
          openPicker: true,
          expand: "lesson",
          text: "Add a video. It uses the same body as a reading, and it stays incomplete until a video is in that body.",
        }),
        details({
          id: "add-audio",
          target: "cb-type-audio",
          fallback: ["cb-add-lesson"],
          openPicker: true,
          expand: "lesson",
          text: "Audio matches video. Instructions in the body, and an audio file instead of a video.",
        }),
        details({
          id: "media-body",
          target: "cb-item-body",
          fallback: editorFallbacks(["video", "audio"]),
          editorTypes: ["video", "audio"],
          expand: "lesson",
          text: "Put the file in the body. Continue settings can also make them watch a video to the end. YouTube and Vimeo cannot be locked that way.",
        }),
      ],
    },
    {
      id: "slides",
      title: "Slides",
      steps: [
        details({
          id: "add-slides",
          target: "cb-type-slides",
          fallback: ["cb-add-lesson"],
          openPicker: true,
          expand: "lesson",
          text: "Add slides. The body is only the instructions. The deck itself is separate.",
        }),
        details({
          id: "slides-deck",
          target: "cb-item-slides",
          fallback: editorFallbacks(["slides"]),
          editorTypes: ["slides"],
          expand: "lesson",
          text: "A deck is a Canva link or a PDF. It can stay on this item or be mapped onto lessons.",
        }),
      ],
    },
    {
      id: "terms",
      title: "Key terms",
      steps: [
        details({
          id: "add-terms",
          target: "cb-type-key_terms",
          fallback: ["cb-add-lesson"],
          openPicker: true,
          expand: "lesson",
          text: "Add key terms. This one is a list, not a reading.",
        }),
        details({
          id: "terms-list",
          target: "cb-item-terms",
          fallback: editorFallbacks(["key_terms"]),
          editorTypes: ["key_terms"],
          expand: "lesson",
          pose: "book",
          text: "Each row is a term and a definition. Seven to ten is the usual set. It is incomplete with none.",
        }),
      ],
    },
    {
      id: "questions",
      title: "Questions",
      steps: [
        details({
          id: "add-assessment",
          target: "cb-type-assessment",
          fallback: ["cb-add-lesson"],
          openPicker: true,
          expand: "lesson",
          text: "Add an assessment. It is a formative check. Homework and an exam use this same question builder.",
        }),
        details({
          id: "add-homework",
          target: "cb-type-homework",
          fallback: ["cb-add-lesson"],
          openPicker: true,
          expand: "lesson",
          text: "Add a homework. Same questions as the assessment. It is practice.",
        }),
        details({
          id: "add-exam",
          target: "cb-type-exam",
          fallback: ["cb-add-lesson"],
          openPicker: true,
          expand: "lesson",
          text: "Add an exam. Same question list again. This is the summative one.",
        }),
        details({
          id: "question-list",
          target: "cb-item-questions",
          fallback: editorFallbacks(["assessment", "homework", "exam"]),
          editorTypes: ["assessment", "homework", "exam"],
          expand: "lesson",
          pose: "laptop",
          text: "Points live on each question, not on the activity. Add at least one question or it stays incomplete. Due date and max attempts are on the form. Empty max attempts means unlimited.",
        }),
        details({
          id: "submission",
          target: "cb-submission-panel",
          fallback: ["cb-item-submission", "cb-item-questions"],
          editorTypes: ["assessment", "homework", "exam"],
          editorPanel: "submission",
          expand: "lesson",
          text: "Submission settings show only when the activity has questions. After they submit, right and wrong, the answer key, and the point totals stay off until you turn them on. This is not what lets them leave.",
        }),
      ],
    },
    {
      id: "handin",
      title: "Project & lab",
      steps: [
        details({
          id: "add-project",
          target: "cb-type-project",
          fallback: ["cb-add-lesson"],
          openPicker: true,
          expand: "lesson",
          text: "Add a project. The student hands work in. A lab is the same editor.",
        }),
        details({
          id: "add-lab",
          target: "cb-type-lab",
          fallback: ["cb-add-lesson"],
          openPicker: true,
          expand: "lesson",
          text: "Add a lab. Same hand-in as the project. Use the body for directions and materials.",
        }),
        details({
          id: "handin-points",
          target: "cb-item-points",
          fallback: editorFallbacks(["project", "lab"]),
          editorTypes: ["project", "lab"],
          expand: "lesson",
          text: "Points start at 10, marked by hand. 0 means it is not graded. You can instead award them when the student completes it.",
        }),
      ],
    },
    {
      id: "discussion",
      title: "Discussion",
      steps: [
        details({
          id: "add-discussion",
          target: "cb-type-discussion",
          fallback: ["cb-add-lesson"],
          openPicker: true,
          expand: "lesson",
          text: "Add a discussion. The body is the prompt students reply to.",
        }),
        details({
          id: "discussion-points",
          target: "cb-item-points",
          fallback: editorFallbacks(["discussion"]),
          editorTypes: ["discussion"],
          expand: "lesson",
          text: "Points stay on so a discussion can sit in the grade weights. You mark each prompt post, or award the points when they complete it.",
        }),
      ],
    },
    {
      id: "continue",
      title: "Continue",
      steps: [
        details({
          id: "continue-button",
          target: "cb-item-continue",
          fallback: editorFallbacks(["*"]),
          editorTypes: ["*"],
          expand: "lesson",
          text: "Continue settings are on every activity you can edit. This is what they must do before they can leave the step.",
        }),
        details({
          id: "continue-panel",
          target: "cb-continue-panel",
          fallback: ["cb-item-continue"],
          editorTypes: ["*"],
          editorPanel: "continue",
          expand: "lesson",
          text: "Complete is always required and always last. Minimum time starts off. Watch the video appears only when there is a video. A minimum score appears only when there are questions and Require Knowledge is on. A discussion can also require replies.",
        }),
      ],
    },
    ...settings,
    {
      id: "done",
      title: "Done",
      steps: [
        details({
          id: "done",
          target: "cb-outline",
          pose: "celebrating",
          expand: "lesson",
          text: "That is the standard set: one unit, one lesson inside it, and one of each activity in the lesson. More units and lessons work the same way.",
        }),
      ],
    },
  ];
}

export function flattenWalkthrough(chapters: WalkChapter[]) {
  return chapters.flatMap((chapter) => chapter.steps);
}

export function walkOutline(chapters: WalkChapter[]) {
  let index = 0;
  return chapters.map((chapter) => {
    const entry = { id: chapter.id, title: chapter.title, index };
    index += chapter.steps.length;
    return entry;
  });
}

export function walkChapterAt(chapters: WalkChapter[], stepIndex: number) {
  let index = 0;
  for (const chapter of chapters) {
    if (stepIndex < index + chapter.steps.length) return chapter;
    index += chapter.steps.length;
  }
  return chapters[chapters.length - 1];
}

function storageKey(userId: string | number) {
  return `${STORAGE_KEY}:${userId}`;
}

export function walkthroughRecord(userId: string | number | null | undefined): WalkthroughRecord | null {
  if (userId == null || userId === "") return null;
  try {
    const raw = localStorage.getItem(storageKey(userId));
    return raw === "dismissed" || raw === "done" ? raw : null;
  } catch {
    return null;
  }
}

export function rememberWalkthrough(userId: string | number | null | undefined, record: WalkthroughRecord) {
  if (userId == null || userId === "") return;
  try {
    localStorage.setItem(storageKey(userId), record);
  } catch {
    // Private mode still honors the choice for this visit.
  }
}
